import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface VideoSyncFunctions {
  registerVideo: (el: HTMLVideoElement) => () => void;
  notifyPlay: (source: HTMLVideoElement) => void;
  notifyPause: (source: HTMLVideoElement) => void;
  notifySeek: (source: HTMLVideoElement, time: number) => void;
  isSourceAction: (el: HTMLVideoElement) => boolean;
}

export interface VideoSyncState {
  syncEnabled: boolean;
  setSyncEnabled: (v: boolean) => void;
  videosReady: boolean;
}

export const VideoSyncFunctionsContext = createContext<VideoSyncFunctions | null>(null);
export const VideoSyncStateContext = createContext<VideoSyncState | null>(null);

const END_OF_VIDEO_THRESHOLD = 0.3;

export function CompareViewProvider({ children }: { children: ReactNode }) {
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [videosReady, setVideosReady] = useState(false);

  const syncEnabledRef = useRef(false);
  syncEnabledRef.current = syncEnabled;

  const videosRef = useRef(new Set<HTMLVideoElement>());
  const programmaticRef = useRef(new Set<HTMLVideoElement>());

  const registerVideo = useCallback((el: HTMLVideoElement) => {
    videosRef.current.add(el);
    if (videosRef.current.size >= 2) setVideosReady(true);
    return () => {
      videosRef.current.delete(el);
      programmaticRef.current.delete(el);
      if (videosRef.current.size < 2) setVideosReady(false);
    };
  }, []);

  const isSourceAction = useCallback(
    (el: HTMLVideoElement) => !programmaticRef.current.has(el),
    [],
  );

  const syncOthers = useCallback(
    (
      source: HTMLVideoElement,
      guard: (el: HTMLVideoElement) => boolean,
      act: (el: HTMLVideoElement) => Promise<void> | void,
    ) => {
      if (!syncEnabledRef.current) return;
      videosRef.current.forEach((el) => {
        if (el !== source && guard(el)) {
          programmaticRef.current.add(el);
          const result = act(el);
          if (result instanceof Promise) {
            void result.finally(() => programmaticRef.current.delete(el));
          } else {
            programmaticRef.current.delete(el);
          }
        }
      });
    },
    [],
  );

  const notifyPlay = useCallback(
    (source: HTMLVideoElement) =>
      syncOthers(
        source,
        (el) => el.paused,
        (el) => el.play(),
      ),
    [syncOthers],
  );

  const notifyPause = useCallback(
    (source: HTMLVideoElement) => {
      if (
        source.duration > 0 &&
        (source.duration - source.currentTime < END_OF_VIDEO_THRESHOLD || source.ended)
      )
        return;
      syncOthers(
        source,
        (el) => !el.paused,
        (el) => el.pause(),
      );
    },
    [syncOthers],
  );

  const notifySeek = useCallback((source: HTMLVideoElement, time: number) => {
    if (!syncEnabledRef.current) return;
    videosRef.current.forEach((el) => {
      if (el !== source) {
        if (!programmaticRef.current.has(el)) {
          el.addEventListener('seeked', () => programmaticRef.current.delete(el), { once: true });
        }
        programmaticRef.current.add(el);
        el.currentTime = time;
      }
    });
  }, []);

  useEffect(() => {
    if (!syncEnabled) return;
    const [source, ...others] = [...videosRef.current];
    const time = source.currentTime;
    [...others, source].forEach((el) => {
      programmaticRef.current.add(el);
      if (!el.paused) el.pause();
      el.currentTime = time;
      programmaticRef.current.delete(el);
    });
  }, [syncEnabled]);

  const functions = useMemo<VideoSyncFunctions>(
    () => ({ registerVideo, isSourceAction, notifyPlay, notifyPause, notifySeek }),
    [registerVideo, isSourceAction, notifyPlay, notifyPause, notifySeek],
  );

  const state = useMemo<VideoSyncState>(
    () => ({ syncEnabled, setSyncEnabled, videosReady }),
    [syncEnabled, videosReady],
  );

  return (
    <VideoSyncFunctionsContext.Provider value={functions}>
      <VideoSyncStateContext.Provider value={state}>{children}</VideoSyncStateContext.Provider>
    </VideoSyncFunctionsContext.Provider>
  );
}
